import { BLACK_FRIDAY_CONFIG } from "@/lib/campaigns/black-friday/config";
import { isBlackFridayPricingPaused } from "./black-friday-pricing-activity";
export type BlackFridayPricingStatus = "unchanged" | "updated" | "normal-price" | "unavailable";
const endedKey = `${BLACK_FRIDAY_CONFIG.key}:pricing-sync-ended`;
const inFlight = new Map<string, Promise<BlackFridayPricingStatus>>();
function syncEnded(): boolean {
	try {
		return typeof window !== "undefined" && window.localStorage.getItem(endedKey) === "1";
	} catch {
		return false;
	}
}
async function request(body: { checkoutId: string; code?: string }) {
	const response = await fetch("/api/campaigns/black-friday/checkout-pricing", {
		method: "POST",
		credentials: "same-origin",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(body),
		signal: AbortSignal.timeout(20_000),
	});
	if (!response.ok) throw new Error("pricing_unavailable");
	const result = (await response.json()) as {
		ok?: boolean;
		status?: BlackFridayPricingStatus;
		codeAllowed?: boolean;
		syncDisabled?: boolean;
	};
	if (!result.ok) throw new Error("pricing_unavailable");
	if (result.syncDisabled)
		try {
			window.localStorage.setItem(endedKey, "1");
		} catch {
			/* Storage may be disabled. Server no-op remains cheap. */
		}
	return result;
}
/** Device clocks never decide eligibility. The server supplies the end-of-cleanup hint. */
export function requestBlackFridayPricing(
	checkoutId: string,
	options: { paymentStart?: boolean } = {},
): Promise<BlackFridayPricingStatus> {
	if (syncEnded()) return Promise.resolve("unchanged");
	const pending = inFlight.get(checkoutId);
	if (pending) return pending;
	if (!options.paymentStart && isBlackFridayPricingPaused(checkoutId)) return Promise.resolve("unchanged");
	const job = request({ checkoutId })
		.then((result) => result.status ?? ("unavailable" as BlackFridayPricingStatus))
		.catch(() => "unavailable" as const)
		.finally(() => inFlight.delete(checkoutId));
	inFlight.set(checkoutId, job);
	return job;
}
export async function requestBlackFridayCodeAllowed(checkoutId: string, code: string): Promise<boolean> {
	if (syncEnded()) return true;
	try {
		return (await request({ checkoutId, code })).codeAllowed !== false;
	} catch {
		return true;
	}
}
